import { getConsentStatus } from "./consent";
import { whenMarketingMayLoad } from "./marketing-load";

// Reddit Pixel + Conversions API relay.
//
// Every event is fired twice with the same conversionId: once through the
// browser pixel (rdt) and once through /api/reddit/event, which forwards it to
// the Reddit Conversions API with IP / user agent / click ID. Reddit
// deduplicates the pair. Purchases and quote leads pass `serverHandled`: the
// server sends those itself from verified order / lead data.

const PIXEL_ID = (import.meta.env.VITE_REDDIT_PIXEL_ID as string | undefined)?.trim() || "";

export type RedditPixelEvent =
  | "PageVisit"
  | "ViewContent"
  | "Search"
  | "AddToCart"
  | "AddToWishlist"
  | "Purchase"
  | "Lead"
  | "SignUp";

const CAPI_TYPE: Record<RedditPixelEvent, string> = {
  PageVisit: "PAGE_VISIT",
  ViewContent: "VIEW_CONTENT",
  Search: "SEARCH",
  AddToCart: "ADD_TO_CART",
  AddToWishlist: "ADD_TO_WISHLIST",
  Purchase: "PURCHASE",
  Lead: "LEAD",
  SignUp: "SIGN_UP",
};

// Reddit flags value / currency / itemCount as unsupported metadata on these
// events; they only accept products.
const PRODUCTS_ONLY: ReadonlySet<RedditPixelEvent> = new Set<RedditPixelEvent>(["PageVisit", "ViewContent", "Search"]);

export interface RedditProduct {
  id: string;
  name?: string;
  category?: string;
  quantity?: number;
  itemPrice?: number;
}

export interface RedditTrackOptions {
  value?: number;
  currency?: string;
  itemCount?: number;
  products?: RedditProduct[];
  /** Dedup key shared with the server event. Generated when omitted. */
  conversionId?: string;
  /** Improves matching for the server copy (hashed server-side). */
  email?: string;
  /** The server already sends this event (purchase, quote lead) — pixel only. */
  serverHandled?: boolean;
}

declare global {
  interface Window {
    rdt?: ((...args: unknown[]) => void) & { callQueue?: unknown[][]; sendEvent?: (...args: unknown[]) => void };
  }
}

function isEnabled(): boolean {
  if (typeof window === "undefined" || !PIXEL_ID) return false;
  try {
    return getConsentStatus() !== "opted-out";
  } catch {
    return true;
  }
}

function newConversionId(event: RedditPixelEvent): string {
  const rand = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${event.toLowerCase()}_${rand}`;
}

let initialized = false;

/**
 * Installs the rdt queue stub immediately (so events fired during hydration
 * are queued), downloads pixel.js on the shared marketing-load trigger (first
 * interaction, or 4 s after window load), and records the landing
 * PageVisit. Safe to call more than once; no-op without a pixel ID or when the
 * visitor opted out of tracking cookies.
 */
export function initRedditPixel(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;
  try {
    const w = window;
    if (!w.rdt) {
      const stub = function (...args: unknown[]) {
        if (stub.sendEvent) stub.sendEvent(...args);
        else stub.callQueue!.push(args);
      } as NonNullable<Window["rdt"]>;
      stub.callQueue = [];
      w.rdt = stub;
    }
    w.rdt("init", PIXEL_ID, { optOut: false, useDecimalCurrencyValues: true });

    // Screen size for server events (Reddit match key); session cookie.
    document.cookie = `_rdt_sd=${Math.round(screen.width)}x${Math.round(screen.height)}; path=/; SameSite=Lax`;

    whenMarketingMayLoad(() => {
      // Visitor may have opted out between init and the trigger.
      if (!isEnabled()) return;
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://www.redditstatic.com/ads/pixel.js";
      document.head.appendChild(s);
    });

    redditTrack("PageVisit");
  } catch {
    // Analytics must never break the app.
  }
}

function relay(event: RedditPixelEvent, conversionId: string, opts: RedditTrackOptions): void {
  const body = JSON.stringify({
    event: CAPI_TYPE[event],
    conversionId,
    url: window.location.href,
    referrer: document.referrer || undefined,
    email: opts.email,
    metadata: {
      value: opts.value,
      currency: opts.value !== undefined ? opts.currency ?? "USD" : undefined,
      itemCount: opts.itemCount,
      products: opts.products,
    },
  });
  try {
    if (typeof navigator.sendBeacon === "function"
      && navigator.sendBeacon("/api/reddit/event", new Blob([body], { type: "application/json" }))) {
      return;
    }
  } catch { /* fall through */ }
  fetch("/api/reddit/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {});
}

export function redditTrack(event: RedditPixelEvent, opts: RedditTrackOptions = {}): void {
  if (!isEnabled()) return;
  if (!initialized) initRedditPixel();
  try {
    if (PRODUCTS_ONLY.has(event)) {
      opts = { ...opts, value: undefined, currency: undefined, itemCount: undefined };
    }
    const conversionId = opts.conversionId ?? newConversionId(event);
    const metadata: Record<string, unknown> = { conversionId };
    if (opts.value !== undefined && Number.isFinite(opts.value)) {
      metadata.value = opts.value;
      metadata.currency = opts.currency ?? "USD";
    }
    if (opts.itemCount !== undefined) metadata.itemCount = opts.itemCount;
    if (opts.products?.length) {
      metadata.products = opts.products.map((p) => ({
        id: p.id,
        ...(p.name ? { name: p.name } : {}),
        ...(p.category ? { category: p.category } : {}),
      }));
    }
    window.rdt?.("track", event, metadata);
    if (!opts.serverHandled) relay(event, conversionId, opts);
  } catch {
    // Analytics must never break the app.
  }
}
